import React, { useEffect, useState, useContext } from "react";
import { Container, Row, Col, Form, Table, Button } from "react-bootstrap";
import ReactPaginate from "react-paginate";
import moment from "moment";
import { startCase } from "lodash";
import { AiFillMinusCircle, AiFillPlusCircle } from "react-icons/ai";
import { apiGet } from "../utils/apiFetch";
import obj from "../utils/constants";
import apiPath from "../utils/apiPath";
import helpers from "../utils/helpers";
import AuthContext from "../context/AuthContext";

const DATE_FMT = "YYYY-MM-DD";
const DATETIME_FMT = "YYYY-MM-DD HH:mm:ss";
const transparent = { background: "transparent" };

const toFixedWithoutRounding = (value, decimals = 2) => {
  const num = Number(value);
  if (value === null || value === undefined || value === "" || isNaN(num)) {
    return "";
  }
  const factor = Math.pow(10, decimals);
  return (Math.trunc(num * factor) / factor).toFixed(decimals);
};

const legLabel = (count) =>
  count === 1
    ? "Single"
    : count === 2
      ? "Double"
      : count === 3
        ? "Treble"
        : count > 3
          ? "Accumulator"
          : "";

const getNetAmount = (item) =>
  item?.isDeclared
    ? item.result === "loss"
      ? -Number(item?.loseAmount || 0)
      : Number(item?.profitAmount || 0)
    : 0;

const ParlayBetListNew = () => {
  const { user } = useContext(AuthContext);

  const getDefaultFilter = () => ({
    page: 1,
    limit: 10,
    betType: "parlay",
    status: "",
    keyword: "",
    stake: "",
    currency: "",
    userType: user?.userType,
    id: user?._id,
    fromPeriod: moment().subtract(1, "day").format(DATE_FMT),
    toPeriod: moment().format(DATE_FMT),
  });

  const [filter, setFilter] = useState(getDefaultFilter());
  const [data, setData] = useState({});
  const [expandedId, setExpandedId] = useState("");

  // Dates are picked as YYYY-MM-DD; the API receives full day boundaries.
  const buildParams = (activeFilter) => ({
    ...activeFilter,
    fromPeriod: activeFilter.fromPeriod
      ? moment(activeFilter.fromPeriod).startOf("day").format(DATETIME_FMT)
      : "",
    toPeriod: activeFilter.toPeriod
      ? moment(activeFilter.toPeriod).endOf("day").format(DATETIME_FMT)
      : "",
  });

  const getData = async (activeFilter = filter) => {
    const { status, data: response } = await apiGet(
      apiPath.betHistory,
      buildParams(activeFilter),
    );
    if (status === 200 && response.success) {
      setData(response.results);
      setExpandedId("");
    }
  };

  useEffect(() => {
    getData(filter);
  }, [filter.page, filter.limit]);

  const handlePageClick = (event) =>
    setFilter((prev) => ({ ...prev, page: event.selected + 1 }));

  const handleSearch = () => {
    if (filter.page !== 1) {
      // page change triggers the fetch via useEffect
      setFilter((prev) => ({ ...prev, page: 1 }));
    } else {
      getData(filter);
    }
  };

  const applyPreset = (preset) => {
    const day =
      preset === "today"
        ? moment().format(DATE_FMT)
        : moment().subtract(1, "day").format(DATE_FMT);
    const nextFilter = { ...filter, page: 1, fromPeriod: day, toPeriod: day };
    const refetchViaEffect = filter.page !== 1;
    setFilter(nextFilter);
    if (!refetchViaEffect) getData(nextFilter);
  };

  const handleReset = () => {
    const defaults = getDefaultFilter();
    const refetchViaEffect =
      filter.page !== defaults.page || filter.limit !== defaults.limit;
    setFilter(defaults);
    if (!refetchViaEffect) getData(defaults);
  };

  const rows = data?.data || [];
  const totalRecords = data?.totalDocs || 0;
  const pageCount = data?.totalPages || 0;
  const pageTotalPL = rows.reduce((sum, item) => sum + getNetAmount(item), 0);

  const renderResult = (res) =>
    res?.isDeclared ? (
      res?.result === "win" ? (
        <span className="text-success">
          {Math.abs(toFixedWithoutRounding(res?.profitAmount))}
        </span>
      ) : (
        <span className="text-danger">
          ({Math.abs(toFixedWithoutRounding(res?.loseAmount))})
        </span>
      )
    ) : (
      "Not Settled"
    );

  return (
    <div>
      <div className="user_data_div p-1">
        <section className="main-inner-outer py-3">
          <Container fluid>
            <div className="inner-wrapper">
              <div className="common-container">
                <Form onSubmit={(e) => e.preventDefault()}>
                  <div className="bet_status border-0 mb-0">
                    <Row>
                      <Col lg={2} sm={3} className="mb-lg-0 mb-1">
                        <div className="bet-sec">
                          <Form.Label className="mt-2 me-2">
                            Bet Status:
                          </Form.Label>
                          <Form.Select
                            className="small_select"
                            value={filter.status}
                            onChange={(e) =>
                              setFilter({ ...filter, status: e.target.value })
                            }
                          >
                            <option value="">All</option>
                            <option value="completed">Settled</option>
                            <option value="cancelled">Cancelled</option>
                            <option value="voided">Voided</option>
                          </Form.Select>
                        </div>
                      </Col>

                      <Col lg={2} sm={3} className="mb-lg-0 mb-1">
                        <div className="bet-sec bet-period">
                          <Form.Label className="mt-2 me-2">From</Form.Label>
                          <Form.Group className="form-group">
                            <Form.Control
                              type="date"
                              value={filter.fromPeriod}
                              max={moment().format(DATE_FMT)}
                              onChange={(e) =>
                                setFilter({
                                  ...filter,
                                  fromPeriod: e.target.value,
                                })
                              }
                            />
                            <Form.Control
                              className="small_form_control"
                              type="text"
                              placeholder="00:00"
                              disabled
                            />
                          </Form.Group>
                        </div>
                      </Col>

                      <Col lg={2} sm={3} className="mb-lg-0 mb-1">
                        <div className="bet-sec bet-period">
                          <Form.Label className="mt-2 me-2">To</Form.Label>
                          <Form.Group className="form-group">
                            <Form.Control
                              type="date"
                              value={filter.toPeriod}
                              min={filter.fromPeriod || undefined}
                              max={moment().format(DATE_FMT)}
                              disabled={!filter.fromPeriod}
                              onChange={(e) =>
                                setFilter({
                                  ...filter,
                                  toPeriod: e.target.value,
                                })
                              }
                            />
                            <Form.Control
                              className="small_form_control"
                              type="text"
                              placeholder="23:59"
                              disabled
                            />
                          </Form.Group>
                        </div>
                      </Col>

                      <Col lg={2} sm={3} className="mb-lg-0 mb-1">
                        <div className="bet-sec">
                          <Form.Label>Last</Form.Label>
                          <Form.Select
                            className="small_select"
                            value={filter.limit}
                            onChange={(e) =>
                              setFilter({
                                ...filter,
                                limit: Number(e.target.value),
                                page: 1,
                              })
                            }
                          >
                            <option value={10}>10 Txn</option>
                            <option value={25}>25 Txn</option>
                            <option value={50}>50 Txn</option>
                            <option value={100}>100 Txn</option>
                          </Form.Select>
                        </div>
                      </Col>

                      <Col lg={2} sm={3} className="mb-lg-0 mb-1">
                        <div className="bet-sec">
                          <Form.Label className="mt-2 me-2">Stake</Form.Label>
                          <Form.Control
                            type="number"
                            placeholder="Volume"
                            value={filter.stake}
                            onChange={(e) =>
                              setFilter({ ...filter, stake: e.target.value })
                            }
                          />
                        </div>
                      </Col>

                      <Col lg={2} sm={3} className="mb-lg-0 mb-1">
                        <div className="bet-sec">
                          <Form.Label className="mt-2 me-2">Search</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="Search betId"
                            value={filter.keyword}
                            onChange={(e) =>
                              setFilter({ ...filter, keyword: e.target.value })
                            }
                          />
                        </div>
                      </Col>
                    </Row>

                    <Row className="mt-1">
                      <Col lg={2} sm={3} className="mb-lg-0 mb-1">
                        <div className="bet-sec">
                          <Form.Label className="mt-2 me-2">Currency</Form.Label>
                          <Form.Select
                            className="small_select"
                            value={filter.currency}
                            onChange={(e) =>
                              setFilter({ ...filter, currency: e.target.value })
                            }
                          >
                            <option value="">Select Currency</option>
                            <option value="pbu">USD</option>
                          </Form.Select>
                        </div>
                      </Col>
                      <Col lg={6} sm={9} className="mb-lg-0 mb-1">
                        <div className="history-btn mb-3">
                          <ul className="list-unstyled mb-0">
                            <li>
                              <Button
                                className="me-0 theme_light_btn"
                                onClick={() => applyPreset("today")}
                              >
                                Just For Today
                              </Button>
                            </li>
                            <li>
                              <Button
                                className="me-0 theme_light_btn"
                                onClick={() => applyPreset("yesterday")}
                              >
                                From Yesterday
                              </Button>
                            </li>
                            <li>
                              <Button
                                className="theme_light_btn theme_dark_btn"
                                onClick={handleSearch}
                              >
                                Get History
                              </Button>
                            </li>
                            <li>
                              <Button
                                className="theme_light_btn"
                                onClick={handleReset}
                              >
                                Show Default
                              </Button>
                            </li>
                          </ul>
                        </div>
                      </Col>
                    </Row>

                    <div id="noReportMessage" className="mb-0">
                      <p className="p-0 m-0">
                        Parlay Bet List enables you to review the parlay bets
                        you have placed. <br />
                        Specify the time period during which your bets were
                        placed and the status of the bets.
                      </p>
                      <p className="p-0 m-0">
                        Bet List is shown net of commission.
                      </p>
                      <p className="p-0 m-0 mt-1">
                        Total Records: <strong>{totalRecords}</strong>
                        {" | "}
                        Page Total PL:{" "}
                        <strong
                          className={
                            pageTotalPL < 0 ? "text-danger" : "text-success"
                          }
                        >
                          {toFixedWithoutRounding(pageTotalPL)}
                        </strong>
                      </p>
                    </div>
                  </div>
                </Form>

                <div className="account-table account-tabel-style batting-table">
                  <div className="responsive">
                    <Table>
                      <thead>
                        <tr>
                          <th scope="col">PL ID</th>
                          <th scope="col">Bet ID</th>
                          <th scope="col">Bet placed</th>
                          <th scope="col">Bet Settled</th>
                          <th scope="col">IP Address</th>
                          <th scope="col">Type</th>
                          <th scope="col">Odds req.</th>
                          <th scope="col">Stake</th>
                          <th scope="col">Profit/Loss</th>
                          <th scope="col">Status</th>
                          <th scope="col">Remark</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.length > 0 ? (
                          rows.map((item, index) => {
                            const ip =
                              (item?.ipAddress &&
                                item.ipAddress.replace("::ffff:", "")) ||
                              "-";
                            const netAmount = getNetAmount(item);
                            const isOpen =
                              expandedId && item?.matchBetId === expandedId;

                            return (
                              <React.Fragment key={item?._id || index}>
                                <tr>
                                  <td>{item?.clientName || "-"}</td>
                                  <td
                                    style={{
                                      textDecoration: "underline",
                                      color: "#2779bf",
                                    }}
                                  >
                                    {item?.matchBetId || "-"}
                                    <div>
                                      {isOpen ? (
                                        <AiFillMinusCircle
                                          size={16}
                                          style={{ marginRight: "3px" }}
                                          color="rgba(59, 130, 246, 1)"
                                          onClick={() => setExpandedId("")}
                                        />
                                      ) : (
                                        <AiFillPlusCircle
                                          size={16}
                                          style={{ marginRight: "3px" }}
                                          color="rgba(59, 130, 246, 1)"
                                          onClick={() =>
                                            setExpandedId(item?.matchBetId)
                                          }
                                        />
                                      )}
                                    </div>
                                  </td>
                                  <td>
                                    {helpers.dateFormat(
                                      item?.timeInserted,
                                      user?.timeZone,
                                    )}
                                  </td>
                                  <td>
                                    {item?.isDeclared
                                      ? helpers.dateFormat(
                                          item?.settledDate || item?.updatedAt,
                                          user?.timeZone,
                                        )
                                      : ""}
                                  </td>
                                  <td>{ip}</td>
                                  <td>{legLabel(item?.bets?.length)}</td>
                                  <td>
                                    {toFixedWithoutRounding(item?.bhav) || "-"}
                                  </td>
                                  <td style={{ whiteSpace: "nowrap" }}>
                                    {item?.amount} <strong>(USD)</strong>
                                  </td>
                                  <td>
                                    {item?.isDeclared ? (
                                      <span
                                        style={transparent}
                                        className={
                                          netAmount < 0
                                            ? "text-danger"
                                            : "text-success"
                                        }
                                      >
                                        {toFixedWithoutRounding(netAmount)}
                                      </span>
                                    ) : (
                                      <span style={transparent}>...</span>
                                    )}
                                  </td>
                                  <td>
                                    {item?.isDeclared
                                      ? startCase(item?.status || item?.result)
                                      : "Open"}
                                  </td>
                                  <td></td>
                                </tr>

                                {isOpen && (
                                  <tr
                                    style={{ background: "#F3F2F1" }}
                                    className="expand"
                                  >
                                    <td colSpan={11} className="expand_wrap">
                                      <div
                                        style={{ background: "#F3F2F1" }}
                                        className="d-flex justify-content-center align-items-center"
                                      >
                                        <table className="table-commission table-commission-parlay w-75">
                                          <tbody>
                                            <tr className="header">
                                              <th>Sport</th>
                                              <th>Market</th>
                                              <th>Selection</th>
                                              <th>Type</th>
                                              <th>Odds</th>
                                              <th>Win/Loss</th>
                                            </tr>
                                            {item?.bets?.length > 0 ? (
                                              item.bets.map((res, i) => (
                                                <tr
                                                  key={res?._id || i}
                                                  style={{
                                                    display: "table-row",
                                                    background: "white",
                                                  }}
                                                >
                                                  <td>
                                                    {obj.betCheckObj?.[
                                                      res?.eventType
                                                    ] || "-"}
                                                  </td>
                                                  <td>
                                                    {
                                                      obj.betCheckObj?.[
                                                        res?.eventType
                                                      ]
                                                    }
                                                    <span
                                                      style={transparent}
                                                      className="angle_unicode"
                                                    >
                                                      ▸
                                                    </span>
                                                    <strong style={transparent}>
                                                      {res?.matchName}
                                                    </strong>
                                                    <span
                                                      style={transparent}
                                                      className="angle_unicode"
                                                    >
                                                      ▸
                                                    </span>
                                                    {res?.fancyName}
                                                  </td>
                                                  <td>{res?.runnerName}</td>
                                                  <td>
                                                    <span className="back-bg">
                                                      Back
                                                    </span>
                                                  </td>
                                                  <td>
                                                    {toFixedWithoutRounding(
                                                      res?.bhav,
                                                    ) || "-"}
                                                  </td>
                                                  <td>{renderResult(res)}</td>
                                                </tr>
                                              ))
                                            ) : (
                                              <tr>
                                                <td colSpan={6}>No Data!</td>
                                              </tr>
                                            )}
                                          </tbody>
                                        </table>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan={11}>
                              <span>You have no bets in this time period.</span>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>

                  <div className="bottom-pagination">
                    <ReactPaginate
                      breakLabel="..."
                      nextLabel="Next"
                      forcePage={filter.page ? filter.page - 1 : 0}
                      onPageChange={handlePageClick}
                      pageRangeDisplayed={10}
                      pageCount={pageCount}
                      previousLabel="Prev"
                      renderOnZeroPageCount={null}
                      activeClassName="p-1"
                      activeLinkClassName="pagintion-li"
                    />
                  </div>
                </div>
              </div>
            </div>
          </Container>
        </section>
      </div>
    </div>
  );
};

export default ParlayBetListNew;
